import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const COMPLETION_DELAY = 500

// Builds the same three markup shapes the old string templates produced, as DOM nodes rather than
// an innerHTML-assigned string, so a value arriving off the wire (the ActionCable `url`) can never
// be interpreted as markup. `this.element.replaceChildren(...)` replaces the holder's contents the
// same way the innerHTML assignments it replaces did.
const buildExportingProgress = (progress) => {
  const button = document.createElement("button")
  button.className = "btn btn-main exporting-button"
  button.disabled = true
  button.dataset.progress = String(progress)

  const label = document.createElement("span")
  label.textContent = "Exporting..."

  button.appendChild(label)
  return button
}

// The leading apostrophe inside the href is a long-standing typo, kept so the rendered markup
// stays byte-identical to what this widget has always produced.
const buildExportLink = (url) => {
  const link = document.createElement("a")
  link.className = "btn btn-main"
  link.id = "export-button"
  link.setAttribute("href", `'${url}`)
  link.textContent = "Export Data"
  return link
}

const buildDownloadLink = (url) => {
  const link = document.createElement("a")
  link.target = "_blank"
  link.id = "download-button"
  link.className = "btn btn-primary btn-ghost"
  link.setAttribute("href", url)
  link.textContent = "Download Exported Data"
  return link
}

export default class extends Controller {
  initialize() {
    this.onClick = this.onClick.bind(this)
  }

  connect() {
    this.element.addEventListener("click", this.onClick)

    const exportEvent = this.currentExport()
    if (exportEvent && (exportEvent.stage === "waiting" || exportEvent.stage === "in_progress")) {
      this.subscribeToExport(exportEvent.id)
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
  }

  // The campaign dashboard renders this same partial twice, with the same ids, for this widget
  // and for Domain Performance. That one stops propagation from a listener closer to its own
  // mount; delegating from this mount rather than <body> keeps the reverse true without needing a
  // matching stopPropagation here.
  onClick(event) {
    if (event.target.closest("#export-button")) {
      this.start(event)
    } else if (event.target.closest("#download-button")) {
      this.resetToExportLink()
    }
  }

  start(event) {
    event.preventDefault()

    // Read fresh on every click, unlike the Domain Performance widget, which reads it once at
    // connect to dodge a later address-bar rewrite. The campaign id is sent twice independently:
    // off the URL path here, and off the clicked link's own data-exportable-id below.
    const params = $.param({
      range: new URLSearchParams(window.location.search).get("range"),
      filter: { campaign: [window.location.pathname.split("/")[2]] },
      exportable_id: event.target.closest("#export-button").getAttribute("data-exportable-id")
    })
    const url = `${event.target.closest("#export-button").getAttribute("href")}?${params}`

    this.element.replaceChildren(buildExportingProgress(0))

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    })

    fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Export request failed (${response.status})`) }
        return response.json()
      }).
      then(body => {
        // A plain object body has no `.length`, so this guard is always truthy and always
        // subscribes.
        if (body.length !== 0) { this.subscribeToExport(body.export_id) }
      }).
      catch(this.logFailedCall)
  }

  resetToExportLink() {
    this.element.replaceChildren(buildExportLink(this.element.getAttribute("data-export-url")))
  }

  // `data-export` carries the ExportEvent as JSON, the same shape jQuery's `.data()` used to
  // parse for us; `data-export-url` is a plain string, read as-is. Both stay their existing
  // attribute names -- this partial's `stimulus_controller` local is also mounted by
  // scheduler-customer-export (see decisions.md D12), so hard-coding a Values-API-shaped
  // attribute name here would break that mount.
  currentExport() {
    const raw = this.element.getAttribute("data-export")
    return raw ? JSON.parse(raw) : null
  }

  subscribeToExport(exportId) {
    const subscription = App.cable.subscriptions.create(
      { channel: "GiftCustomerExportsChannel", id: exportId },
      { received: (data) => this.receive(subscription, data) }
    )
  }

  receive(subscription, data) {
    // Keeps the highest progress in case subscription messages arrive out of order. Read off the
    // DOM each time and never written back, so this floor never advances past whatever value the
    // button first rendered with.
    const button = this.element.querySelector(".exporting-button")
    const currentProgress = parseInt(button ? button.dataset.progress : undefined)
    const newProgress = currentProgress > data.event.progress ? currentProgress : data.event.progress

    if (data.event.stage === "in_progress" || data.event.stage === "waiting") {
      this.paintProgress(`${100 - newProgress}% bottom`)
    } else if (data.event.stage === "complete") {
      subscription.unsubscribe()
      this.paintProgress("0% bottom")
      setTimeout(() => {
        this.element.replaceChildren(buildDownloadLink(data.event.url))
      }, COMPLETION_DELAY)
    }
  }

  // querySelectorAll + forEach rather than a target, because the holder legitimately renders no
  // exporting button at all (a waiting export shows the Export Data link) and jQuery's set
  // operations no-opped over that. Also not a target because the shared partial
  // (shared/lasso/dashboard/_export_button, decisions.md D12) server-renders this same
  // `.exporting-button` markup directly on an initial "in_progress" page load, without any
  // controller-specific target attribute -- a Target would go stale on that path (pitfalls.md P8).
  paintProgress(position) {
    this.element.querySelectorAll(".exporting-button, .exporting-button span").
      forEach(element => { element.style.backgroundPosition = position })
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
